import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useParams, useNavigate } from "react-router-dom";
import { PvpCategoryField } from "./PvpCategoryField";
import { PvpChoiceField } from "./PvpChoiceField";
import { PvpScopeToggle } from "./PvpScopeToggle";
import { PvpMeasureSummary, PvpSubgoalsField, PvpMainGoalField } from "./PvpSubgoals";
import { LineChart, Database, Keyboard, Layers } from "lucide-react";

const FUTURE = {
  meetplan: {
    icon: LineChart,
    title: "Meetgegevens & grafiek",
    text: "Voer meetgegevens handmatig in en bekijk de grafiek met nulmetingen, interventies, subdoelen en hoofddoel. Een koppeling met een databron via API volgt later.",
    items: [[Keyboard, "Handmatige invoer"], [Database, "API-koppeling"]],
  },
  abc: {
    icon: Layers,
    title: "ABC-analyse module",
    text: "De inhoudelijke ABC-analyse (Antecedenten, Behavior, Consequenties) komt in een aparte module. Beschrijf hier alvast de opzet en organisatie.",
    items: [],
  },
};

function FutureModule({ k }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const f = FUTURE[k];
  if (!f) return null;
  const Icon = f.icon;
  return (
    <div className="border border-dashed border-zinc-400 bg-zinc-50 p-4 mb-6" data-testid={`pvp-future-module-${k}`}>
      <div className="flex items-center gap-2 font-grotesk font-semibold"><Icon className="h-4 w-4" /> {f.title} {k !== "meetplan" && <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 ml-auto">volgt later</span>}</div>
      <p className="text-sm text-zinc-600 mt-1">{f.text}</p>
      {f.items.length > 0 && (
        <div className="flex gap-2 mt-3">
          {f.items.map(([I, l]) => l === "Handmatige invoer" ? (
            <button key={l} type="button" onClick={() => navigate(`/pvp/${id}/metingen`)} className="inline-flex items-center gap-1 border border-zinc-900 bg-zinc-900 text-white px-3 py-1.5 text-xs hover:bg-zinc-700 transition-colors" data-testid="pvp-manual-input-button"><I className="h-3 w-3" /> {l}</button>
          ) : (
            <span key={l} className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-400"><I className="h-3 w-3" /> {l}</span>
          ))}
        </div>
      )}
    </div>
  );
}

const fieldsInOrder = (section) => {
  if (section.key !== "specificatie") return section.fields;
  const i = section.fields.findIndex(([k]) => k === "gedrag");
  const goal = ["hoofddoel", ""];
  return i < 0 ? [goal, ...section.fields] : [...section.fields.slice(0, i + 1), goal, ...section.fields.slice(i + 1)];
};

export const PvpSectionForm = ({ section, values, all = {}, disabled, onChange, pvp, onPvp }) => (
  <div data-testid={`pvp-section-${section.key}`}>
    <FutureModule k={section.key} />
    {section.key === "context" && pvp && <PvpScopeToggle pvp={pvp} disabled={disabled} onPvp={onPvp} />}
    {section.key === "subdoelen" && (
      <>
        <PvpMeasureSummary m={all.meetplan || {}} spec={all.specificatie || {}} />
        <PvpSubgoalsField meetplan={all.meetplan || {}} spec={all.specificatie || {}} values={values} disabled={disabled} onChange={onChange} />
      </>
    )}
    <div className="space-y-6">
      {fieldsInOrder(section).map(([key, label]) => section.key === "meetplan" && key === "categorie" ? (
        <PvpCategoryField key={key} label={label} values={values} disabled={disabled} onChange={onChange} />
      ) : section.key === "meetplan" && key === "tellen_oordelen" ? (
        <PvpChoiceField key={key} field={key} label={label} options={["Tellen", "Oordelen"]} values={values} disabled={disabled} onChange={onChange} />
      ) : section.key === "meetplan" && key === "frequentie" ? (
        <div key={key} className="space-y-1.5" data-testid="pvp-meetplan-frequentie">
          <Label className="text-sm font-medium leading-snug block">Met welke frequentie ga je meten?</Label>
          <p className="text-sm" data-testid="pvp-meetplan-frequentie-value">
            {all.specificatie?.hoofddoel_frequentie
              ? <span className="font-semibold">{all.specificatie.hoofddoel_frequentie}</span>
              : <span className="text-zinc-400">Nog niet gekozen; kies de frequentie bij het hoofddoel in de stap Specificatie.</span>}
          </p>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block pt-1">Toelichting op de frequentie</Label>
          <Textarea value={values[key] || ""} disabled={disabled} onChange={(e) => onChange({ ...values, [key]: e.target.value })} rows={3}
            className="rounded-none border-zinc-400 focus-visible:ring-zinc-900" data-testid="pvp-field-meetplan-frequentie" />
        </div>
      ) : section.key === "specificatie" && key === "hoofddoel" ? (
        <PvpMainGoalField key={key} meetplan={all.meetplan || {}} values={values} disabled={disabled} onChange={onChange} />
      ) : (
        <div key={key} className="space-y-1.5">
          <Label className="text-sm font-medium leading-snug block">{label}</Label>
          <Textarea
            value={values[key] || ""}
            disabled={disabled}
            onChange={(e) => onChange({ ...values, [key]: e.target.value })}
            rows={3}
            className="rounded-none border-zinc-400 focus-visible:ring-zinc-900"
            data-testid={`pvp-field-${section.key}-${key}`}
          />
        </div>
      ))}
    </div>
  </div>
);
